import { Alert, AlertDescription, Button } from '@bpinternal/ui-kit-next'
import { captureException } from '@sentry/react'
import { useQueryErrorResetBoundary } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { Clipboard } from 'lucide-react'
import React, { ComponentProps, PropsWithChildren, ReactNode, Ref, Suspense, useState } from 'react'
import { ErrorBoundary, FallbackProps } from 'react-error-boundary'
import ErrorIcon from '~/assets/website-maintenance-15.svg?react'
import { showBugReportDialog } from '~/components/BugReportDialog'
import { useCurrentRouteId, useCurrentUser } from '~/hooks'
import { trackEvent } from '~/providers'
import { logout } from '~/services'
import { config, isHttpUnauthorizedError } from '~/shared'
import { copyToClipboard } from '~/utils'
import { Spinner } from '../elements'
import { EmptyState } from '../elements/EmptyState'

type Props = PropsWithChildren<{
  ref?: Ref<ErrorBoundary>
  onError?: (props: FallbackProps) => React.ReactNode
  suspenseFallback?: ComponentProps<typeof Suspense>['fallback']
  width?: string | number
  height?: string | number
  className?: string
  loaderSize?: ComponentProps<typeof Spinner>['size']
  showErrorIcon?: boolean
  errorIconSize?: ComponentProps<typeof EmptyState>['iconSize']
  loader?: ReactNode
}>

export const Boundary = ({
  children,
  onError,
  suspenseFallback,
  width,
  height,
  className,
  loaderSize,
  loader,
  showErrorIcon,
  errorIconSize,
}: Props) => {
  const { reset } = useQueryErrorResetBoundary()
  const params = useParams({ strict: false })
  const routeId = useCurrentRouteId()
  const { profilePicture: _, ...user } = useCurrentUser()

  const defaultOnErrorFallback = ({ resetErrorBoundary, error }: FallbackProps) => {
    trackEvent({ type: 'error_event', routeId, userEmail: user.email, userId: user.id, ...params })
    captureException(error, { tags: { routeId, ...params } })
    if (isHttpUnauthorizedError(error) && !config.usePat) {
      // eslint-disable-next-line no-console
      console.debug('Unauthorized error received from API, logging out...')
      logout()
      return null
    }

    return (
      <DefaultErrorEmptyState
        {...{ width, height, className }}
        error={error}
        className="gap-5"
        icon={showErrorIcon ? undefined : () => null}
        iconSize={errorIconSize}
        primaryAction={
          <Button variant="secondary" onClick={resetErrorBoundary}>
            Retry
          </Button>
        }
      />
    )
  }
  const defaultSuspenseFallback = (
    <div className={`flex items-center justify-center ${className || ''}`} style={{ width, height }}>
      {loader || loader === null ? loader : <Spinner size={loaderSize} />}
    </div>
  )

  return (
    <ErrorBoundary onReset={reset} fallbackRender={onError ?? defaultOnErrorFallback}>
      <Suspense fallback={suspenseFallback === undefined ? defaultSuspenseFallback : suspenseFallback}>
        {children}
      </Suspense>
    </ErrorBoundary>
  )
}

export const DefaultErrorEmptyState = ({
  icon = ErrorIcon,
  description = 'Something went wrong. Please try again later.',

  error,
  primaryAction,
  secondaryAction,
  ...props
}: ComponentProps<typeof EmptyState> & { error: any }) => {
  const [reportId, setReportId] = useState<string>()

  return (
    <EmptyState
      {...props}
      className="gap-5"
      icon={icon}
      primaryAction={reportId ? null : primaryAction}
      description={
        reportId ? (
          <Alert
            variant="default"
            className="cursor-pointer hover:bg-accent"
            onClick={() => void copyToClipboard(reportId, 'Issue ID')}
          >
            <Clipboard className="h-4 w-4" />
            <AlertDescription>
              Your issue has been reported. Click here to copy the issue ID, this will help us track it.
            </AlertDescription>
          </Alert>
        ) : (
          description
        )
      }
      secondaryAction={
        reportId
          ? null
          : secondaryAction ?? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => showBugReportDialog({ error, onSubmit: ({ id }) => setReportId(id) })}
              >
                Report Issue
              </Button>
            )
      }
    />
  )
}
